import React, { useEffect, useState } from 'react';
import ReservationTable from 'components/management/tables/ReservationTable';
import { AdminReservationDto } from 'services/data-contracts';
import { useStoreWrapper } from 'stores/StoreHelper';
import { useParams, useLocation } from 'react-router-dom';
import WeeklyCalendar from 'components/WeeklyCalendar';
import ReservationPeriodFilter from 'components/management/filters/ReservationPeriodFilter';
import ReservationDailyFilter from 'components/management/filters/ReservationDailyFilter';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import ReservationDetail from 'components/management/ReservationDetail';
import { Pagination } from '@mui/material';
import { Row } from 'saladsoft-primitive';
import { ReservationPeriodFilterType } from 'types/CommonTypes';
import moment from 'moment';
import { getUrlParam } from 'libs/helper/utils';
import { setUrlParams } from 'libs/helper/paramsUtil';
import ServiceHelper from 'stores/ServiceHelper';
import { ApiResponse } from 'services/http-client';

interface StatusProps {
  status: 'NEW' | 'CONFIRMED' | 'FAILED' | 'CANCELLED' | 'COMPLETED';
}

const ReservationPeriodPage = observer(() => {
  const defaultDataRange = 3;

  const { uiStore, reservationFilterStore } = useStoreWrapper();
  const { reservationType } = useParams();
  const location = useLocation();

  const getStatusText = (status: StatusProps['status']) => {
    switch (status) {
      case 'NEW':
        return '예약완료';
      case 'CONFIRMED':
        return '확정';
      case 'FAILED':
        return '실패';
      case 'CANCELLED':
        return '취소';
      case 'COMPLETED':
        return '완료';
      default:
        return '알 수 없음';
    }
  };

  const handleRowClick = (reservation: AdminReservationDto) => {
    uiStore?.modal.show({
      title: (
        <div>
          {reservation.reservationNo}{' '}
          <Status status={reservation.status as StatusProps['status']}>
            {getStatusText(reservation.status as StatusProps['status'])}
          </Status>
        </div>
      ),
      style: { maxWidth: '1200px', height: '100%', maxHeight: '80vh' },
      children: <ReservationDetail reservation={reservation} />,
    });
  };

  const handleClickExcel = async (filter: ReservationPeriodFilterType) => {
    console.log(filter);
    try {
      const response = (await ServiceHelper?.adminReservationService?.exportReservationsByPeriod(
        {
          fromDate: filter.dateRange[0] || '',
          toDate: filter.dateRange[1] || '',
          periodType:
            filter.periodType === 'ALL' ? 'RESERVATION' : filter.periodType || 'RESERVATION',
        },
        {
          format: 'blob',
        },
      )) as unknown as ApiResponse<Blob>;

      if (!response?.response?.data) {
        return;
      }
      const blob = response.response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reservations_${moment().format('YYYYMMDD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    }
  };

  useEffect(() => {
    let from = getUrlParam('startDate');
    let to = getUrlParam('endDate');
    const periodType = getUrlParam('periodType');
    const search = getUrlParam('search');
    const page = getUrlParam('page');
    const size = getUrlParam('size');
    if (!from || !to) {
      from = moment().format('YYYY-MM-DD');
      to = moment().add(defaultDataRange, 'days').format('YYYY-MM-DD');

      setUrlParams({
        startDate: from,
        endDate: to,
      });
    }

    reservationFilterStore.getReservationByPeriod({
      periodType: (periodType as ReservationPeriodFilterType['periodType']) || 'RESERVATION',
      status: 'ALL',
      dateRange: [from, to],
      search: search || '',
      page: page ? parseInt(page) : undefined,
      size: size ? parseInt(size) : 40,
    });
  }, [location.search]);

  return (
    <div style={{ padding: '0 20px', marginTop: 10 }}>
      <ReservationPeriodFilter
        onClickExcel={(filter) => {
          handleClickExcel(filter);
        }}
      />
      <ReservationTable
        reservations={reservationFilterStore.reservations}
        onRowClick={handleRowClick}
      />
      {reservationFilterStore.reservations.length === 0 && (
        <EmptyText>검색 결과가 없습니다.</EmptyText>
      )}
    </div>
  );
});

const EmptyText = styled.div`
  text-align: center;
  margin-top: 20px;
`;

const Status = styled.span<StatusProps>`
  display: inline-flex;
  align-items: center;
  padding: 4px 30px;
  border-radius: 12px;
  font-size: 13px;
  justify-content: flex-start;
  font-weight: 500;
  background-color: ${(props) => {
    if (props.status === 'NEW') return '#e6f7e6';
    if (props.status === 'CONFIRMED') return 'rgb(230, 247, 255)';
    if (props.status === 'FAILED') return '#fff8e6';
    if (props.status === 'CANCELLED') return 'rgb(255, 241, 240)';
    if (props.status === 'COMPLETED') return '#f5f5f5';
    return '#f5f5f5';
  }};
  color: ${(props) => {
    if (props.status === 'NEW') return '#2a8d2a';
    if (props.status === 'CONFIRMED') return '#0067a7';
    if (props.status === 'FAILED') return '#c19c00';
    if (props.status === 'CANCELLED') return 'rgb(255, 77, 79)';
    if (props.status === 'COMPLETED') return '#666';
    return '#666';
  }};
`;

export default ReservationPeriodPage;
